import { useNavigate } from 'react-router-dom';
import { useState, useCallback, useMemo } from 'react';
import { Link2, User } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  type SortDirection,
} from '../ui/Table';
import { SeverityBadge } from '../ui/SeverityBadge';
import { Badge } from '../ui/Badge';
import { cn, formatRelativeTime, getRiskScoreColor } from '../../lib/utils';
import {
  sortIncidents,
  getIncidentKey,
  type SortField,
  type QueueSortField,
} from './incidentQueueSort';
import type { EnrichedIncident } from '../../data/types';

interface IncidentTableProps {
  incidents: EnrichedIncident[];
  // Selection props
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
}

export function IncidentTable({
  incidents,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
}: IncidentTableProps) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<QueueSortField>('queue');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedIncidents = sortIncidents(incidents, sortField, sortDirection);

  const getSortDirection = (field: SortField): SortDirection => {
    return sortField === field ? sortDirection : null;
  };

  const handleRowClick = (incident: EnrichedIncident) => {
    navigate(`/incidents/${incident.tenantId}/${incident.incidentId}`);
  };

  // Selection handlers
  const allIncidentKeys = useMemo(
    () => sortedIncidents.map(getIncidentKey),
    [sortedIncidents]
  );

  const isAllSelected = useMemo(
    () => allIncidentKeys.length > 0 && allIncidentKeys.every((key) => selectedIds.has(key)),
    [allIncidentKeys, selectedIds]
  );

  const isSomeSelected = useMemo(
    () => allIncidentKeys.some((key) => selectedIds.has(key)) && !isAllSelected,
    [allIncidentKeys, selectedIds, isAllSelected]
  );

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;

    if (isAllSelected) {
      // Deselect all visible incidents
      const newSelection = new Set(selectedIds);
      allIncidentKeys.forEach((key) => newSelection.delete(key));
      onSelectionChange(newSelection);
    } else {
      // Select all visible incidents
      const newSelection = new Set(selectedIds);
      allIncidentKeys.forEach((key) => newSelection.add(key));
      onSelectionChange(newSelection);
    }
  }, [isAllSelected, selectedIds, allIncidentKeys, onSelectionChange]);

  const handleSelectRow = useCallback(
    (incident: EnrichedIncident) => {
      if (!onSelectionChange) return;

      const key = getIncidentKey(incident);
      const newSelection = new Set(selectedIds);

      if (newSelection.has(key)) {
        newSelection.delete(key);
      } else {
        newSelection.add(key);
      }

      onSelectionChange(newSelection);
    },
    [selectedIds, onSelectionChange]
  );

  return (
    <Table>
      <TableHeader>
        <TableRow hover={false}>
          {selectable && (
            <TableHead className="w-14 pr-0">
              <div className="flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isSomeSelected;
                  }}
                  onChange={handleSelectAll}
                  className="w-5 h-5 rounded border-border-default text-brand-blue focus:ring-brand-blue cursor-pointer"
                  aria-label="Select all incidents"
                />
              </div>
            </TableHead>
          )}
          <TableHead
            sortable
            sortDirection={getSortDirection('severity')}
            onSort={() => handleSort('severity')}
          >
            Severity
          </TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Tenant</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Assigned</TableHead>
          <TableHead
            sortable
            sortDirection={getSortDirection('riskScore')}
            onSort={() => handleSort('riskScore')}
          >
            Risk
          </TableHead>
          <TableHead
            sortable
            sortDirection={getSortDirection('createdTime')}
            onSort={() => handleSort('createdTime')}
          >
            Created
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedIncidents.map((incident) => {
          const key = getIncidentKey(incident);
          const isSelected = selectedIds.has(key);

          return (
            <TableRow
              key={key}
              onClick={() => handleRowClick(incident)}
              className={cn(isSelected && 'bg-blue-50 dark:bg-blue-900/20')}
            >
              {selectable && (
                <TableCell className="w-14 pr-0">
                  <div
                    className="flex items-center justify-center w-10 h-10 -my-2 cursor-pointer hover:bg-bg-tertiary rounded-lg transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectRow(incident);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-5 h-5 rounded border-border-default text-brand-blue focus:ring-brand-blue cursor-pointer"
                      aria-label={`Select incident ${incident.incidentId}`}
                    />
                  </div>
                </TableCell>
              )}
              <TableCell>
                <SeverityBadge severity={incident.severity} />
              </TableCell>
              <TableCell>
                <div className="max-w-md">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                    <span className="min-w-0 truncate">
                      {incident.title}
                    </span>
                    {(incident.newAlertCount ?? 0) > 0 && (
                      <Badge variant="blue" className="shrink-0 whitespace-nowrap">
                        +{incident.newAlertCount} new alert{incident.newAlertCount === 1 ? '' : 's'}
                      </Badge>
                    )}
                    {incident.playbookId && (
                      <span title="Linked to playbook">
                        <Link2 className="w-3.5 h-3.5 text-text-muted" />
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text-secondary font-mono mt-0.5">
                    {incident.incidentId}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <span className="text-sm">{incident.tenantDisplayName || incident.tenantId}</span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant={
                      ({
                        new: 'orange',
                        acknowledged: 'blue',
                        investigating: 'purple',
                        closed: 'green',
                        reopened: 'red',
                      } as const)[incident.platformStatus] ?? 'default'
                    }
                  >
                    {incident.platformStatus}
                  </Badge>
                  {incident.platformStatus === 'closed' &&
                    incident.defenderStatus?.toLowerCase() === 'resolved' &&
                    !incident.assignedToUserId && (
                    <Badge variant="default">
                      auto-resolved
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {incident.assignedToName ? (
                  <span className="flex items-center gap-1.5 text-sm">
                    <User className="w-3.5 h-3.5 text-text-muted" />
                    {incident.assignedToName}
                  </span>
                ) : (
                  <span className="text-text-secondary text-sm">Unassigned</span>
                )}
              </TableCell>
              <TableCell>
                {incident.aiEnrichment?.riskScore !== undefined ? (
                  <span
                    className="font-mono font-bold"
                    style={{ color: getRiskScoreColor(incident.aiEnrichment.riskScore) }}
                  >
                    {incident.aiEnrichment.riskScore.toFixed(1)}
                  </span>
                ) : (
                  <span className="text-text-secondary">—</span>
                )}
              </TableCell>
              <TableCell mono>
                <span className="text-text-secondary">
                  {formatRelativeTime(incident.createdTime)}
                </span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
